import "@arco-design/web-react/es/Image/style";
import _Image from "@arco-design/web-react/es/Image";
import "@arco-design/web-react/es/Input/style";
import _Input from "@arco-design/web-react/es/Input";
import "@arco-design/web-react/es/Form/style";
import _Form from "@arco-design/web-react/es/Form";
import html2jsx from 'html2jsx';
import { flowExcelStore } from '@/store/flowExcelStore';
import * as React from 'react';
import { transform } from '@babel/standalone';
import Decimal from 'decimal.js';
import DatePicker from '@/components/DatePicker';
import ListSelector from '@/components/ListSelector';
import { LxInputNumber } from '@/components/InputNumber';
import EnumSelector from '@/components/EnumSelector';
import LongText from '@/components/LongText';
import { formMap, viewMap } from './formItem';
import { formulas } from './formula';
import FilesUpload from '@/components/FilesUpload';
import { RadioGroup } from '@/components/RadioGroup';
import { RULE_TYPES } from '@/types/ruleType';
import { EmptyCheckBoxIcon, RadioIcon } from '@/components/Icons';

/**
 * 判断一个字段名是否需要转成「数组」结构。
 * 规则：字段名（去掉 @N 索引后的小写）以 `items` 或 `list` 结尾 → 数组；其他 → 对象。
 *
 * 示例：
 *   items    -> true
 *   itemList -> true
 *   depter   -> false
 *   code     -> false
 */
function isArrayFieldName(rawName) {
  const base = String(rawName ?? '').split('@')[0];
  const lower = base.toLowerCase();
  return /items$/.test(lower) || /list$/.test(lower);
}

/**
 * 把扁平表单数据还原成嵌套对象/数组，支持以下 key 形态：
 *   - `xxx@N`                       —— 顶层标量按行号，单行场景 N=0
 *   - `a.b.c@N`                     —— 用点号串联多层 key，叶子段带 @N（行号）
 *   - `items.serial@0` + `items.name@0` —— 同一父 items 下，serial@0/name@0 表示同一行
 *   - `items@0` / `items@1`         —— 把多个段按行号归位到外层数组（罕见）
 *
 * 识别规则：
 *   - 字段名（剥掉点号尾段、剥掉 @N）以 `items` 或 `list` 结尾 → 数组
 *   - 其他 → 对象（递归剥掉 @N）
 *
 * 数组形父节点里，相同 baseName 的 `xxx@0/xxx@1` 表示「同一字段在不同行」，
 * 必须按行号拆开；对象形父节点里的 `info@0` 只是「单行对象内的字段标记」，
 * `@N` 直接剥掉不保留。
 */
export function convertIndexedFormJson(input) {
  return convertNode(input, false);
}
function convertNode(value, parentIsArray) {
  // 不是对象/数组 → 标量，原样返回
  if (!value || typeof value !== 'object') {
    return value;
  }
  if (Array.isArray(value)) {
    return value;
  }
  const obj = value;
  const objKeys = Object.keys(obj);
  const hasOnlyPlainKeys = objKeys.every(k => k.indexOf('@') < 0);

  // 顶层/父是对象的纯标量子对象（key 都不带 @）：
  //   - 若没有需要剥 @ 的子字段，直接原样返回
  //   - 若子字段是对象且带 @N，要递归剥 @0
  // 通过「value 本身是不是带 @ 子字段的对象」区分；用惰性扫描：
  //   任一 value 是带 @ 字段的对象 → 走逐字段递归分支
  if (!parentIsArray && hasOnlyPlainKeys) {
    const needRecurse = objKeys.some(k => {
      const v = obj[k];
      if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
      // v 是普通对象，看它的子 key 有没有 @N
      return Object.keys(v).some(kk => kk.indexOf('@') >= 0);
    });
    if (!needRecurse) return obj;

    // 逐字段递归：标量保留；带 @ 子对象走剥 @0（按数组/对象形区分）
    const result = {};
    for (const key of objKeys) {
      result[key] = convertNode(obj[key], isArrayFieldName(key));
    }
    return result;
  }
  if (parentIsArray) {
    // 父节点是数组：把 `{ 'xxx@N': v }` 按 N 分组成数组行
    // 例如 { 'serial@0': 1, 'serial@1': 2, 'name@0': 'A', 'name@1': 'B' }
    //  → rows = [ {serial:1, name:'A'}, {serial:2, name:'B'} ]
    // 用嵌套对象 { baseName: { [rowIndex]: value } } 代替 Map 以兼容 es5。
    // 同时支持点号复合 key：父是数组时，按叶子段 baseName + rowIdx 分组。
    const groups = {};
    let rowCount = -1;
    for (const key of Object.keys(obj)) {
      const leaf = splitLeafKey(key);
      if (!groups[leaf.baseName]) groups[leaf.baseName] = {};
      groups[leaf.baseName][leaf.rowIdx] = obj[key];
      if (leaf.rowIdx > rowCount) rowCount = leaf.rowIdx;
    }
    if (rowCount < 0) rowCount = 0;
    const rows = [];
    for (let i = 0; i <= rowCount; i += 1) {
      const row = {};
      for (const baseName of Object.keys(groups)) {
        const idxMap = groups[baseName];
        if (idxMap[i] !== undefined || i in idxMap) {
          row[baseName] = convertNode(idxMap[i], isArrayFieldName(baseName));
        }
      }
      rows.push(row);
    }
    // 过滤整行所有字段都是 undefined / null 的空行
    // （form 表单未填行也会按行号展开成 `{name: undefined, type: undefined, ...}`）
    return rows.filter(row => !isEmptyRow(row));
  }

  // 父节点是对象：把点号复合 key（如 `depter.info@0` / `items.serial@0`）
  // 按点号拆段，逐层写入嵌套对象/数组。
  const result = {};
  for (const key of Object.keys(obj)) {
    const segments = splitPathSegments(key);
    // 把叶子段的 rowIdx 沿路径向下传递，
    // 让中间段数组形（如 `items`）能拿到正确的行号定位。
    const lastIdx = segments.length - 1;
    const inherited = lastIdx >= 0 ? segments[lastIdx].rowIdx : null;
    writeNested(result, segments, obj[key], inherited);
  }
  return result;
}

/**
 * 拆一个 key 路径为段数组。每段 = { name: 段名, rowIdx: 行号或 null }。
 * 例如：
 *   'code@0'                    → [{name:'code', rowIdx:0}]
 *   'items.serial@1'            → [{name:'items', rowIdx:null}, {name:'serial', rowIdx:1}]
 *   'depter.info@0'             → [{name:'depter', rowIdx:null}, {name:'info', rowIdx:0}]
 *   'a.b.c@2'                   → [{name:'a'}, {name:'b'}, {name:'c', rowIdx:2}]
 *   'items@0'                   → [{name:'items', rowIdx:0}]
 */
function splitPathSegments(key) {
  const parts = key.split('.');
  return parts.map(part => {
    const atIdx = part.indexOf('@');
    if (atIdx < 0) {
      return {
        name: part,
        rowIdx: null
      };
    }
    const name = part.slice(0, atIdx);
    const idxStr = part.slice(atIdx + 1);
    const rowIdx = idxStr === '' ? 0 : Number(idxStr);
    return {
      name,
      rowIdx
    };
  });
}

/**
 * 给「父是数组」分支用：从 key 里取叶子段的 baseName + rowIdx。
 */
function splitLeafKey(key) {
  const segments = splitPathSegments(key);
  const leaf = segments[segments.length - 1];
  return {
    baseName: leaf.name,
    rowIdx: leaf.rowIdx ?? 0
  };
}

/**
 * 把 value 按 segments 写入 result 嵌套对象中。
 *   - 中间段若是数组形（items/list）：先确保 result[seg.name] 是数组，
 *     按行号归位到元素，再递归到该元素继续处理后续段。
 *   - 中间段若是对象形：递归到 result[seg.name] 继续处理后续段。
 *   - 叶子段：value 直接放到最后一层，按行号归位（数组）或剥掉行号（对象）。
 *
 * 关键：当路径里第一个数组形中间段后面没有 rowIdx（如 `items.serial@0`），
 * 行号信息在叶子段；递归时把叶子段的 rowIdx 当作「数组行号」传下去。
 */
function writeNested(result, segments, value, inheritedRowIdx = null) {
  if (segments.length === 0) return;

  // 只有一个段：直接在 result 上处理（按数组/对象形区分）
  if (segments.length === 1) {
    // 数组形叶子段如果在前面继承到 rowIdx，用它；否则用段自身 rowIdx。
    const seg = segments[0];
    const isArray = isArrayFieldName(seg.name);
    if (isArray && seg.rowIdx === null && inheritedRowIdx !== null) {
      writeLeaf(result, {
        name: seg.name,
        rowIdx: inheritedRowIdx
      }, value);
    } else {
      writeLeaf(result, seg, value);
    }
    return;
  }

  // 多段：递归中间段
  const [head, ...rest] = segments;
  if (head.name === '') return;
  if (isArrayFieldName(head.name)) {
    // 中间段是数组形：行号取 head.rowIdx；如果没有，沿用 inheritedRowIdx（叶子段的行号）
    const n = head.rowIdx ?? inheritedRowIdx ?? 0;
    if (!Array.isArray(result[head.name])) {
      result[head.name] = [];
    }
    const arr = result[head.name];
    while (arr.length <= n) arr.push(undefined);
    const target = arr[n];
    if (target === undefined || target === null || typeof target !== 'object' || Array.isArray(target)) {
      arr[n] = {};
    }
    // 后续段的行号不再继承（除非后续又有数组形中间段无 rowIdx 的情况，但极罕见）
    writeNested(arr[n], rest, value, null);
    return;
  }

  // 中间段是对象形
  if (head.rowIdx !== null) {
    // 路径中间段不该带行号（行号只能在数组父节点的元素位置出现），
    // 健壮处理：当作行号归位到外层数组上
    if (!Array.isArray(result[head.name])) {
      result[head.name] = [];
    }
    const arr = result[head.name];
    while (arr.length <= head.rowIdx) arr.push(undefined);
    const target = arr[head.rowIdx];
    if (target === undefined || target === null || typeof target !== 'object' || Array.isArray(target)) {
      arr[head.rowIdx] = {};
    }
    writeNested(arr[head.rowIdx], rest, value, null);
    return;
  }
  if (!result[head.name] || typeof result[head.name] !== 'object' || Array.isArray(result[head.name])) {
    result[head.name] = {};
  }
  // 数组形叶子段无 rowIdx 时，沿用之前继承的行号
  writeNested(result[head.name], rest, value, inheritedRowIdx);
}

/**
 * 写入叶子段到 result：
 *   - 数组形 baseName + 带 rowIdx：按行号归位，value 递归成行数组
 *   - 数组形 baseName + 不带 rowIdx：value 整体递归成行数组，覆盖式赋值
 *   - 对象形 baseName + 带 rowIdx：剥掉行号，把 value 写入 result[name]
 *   - 对象形 baseName + 不带 rowIdx：直接把 value 写入 result[name]
 */
function writeLeaf(result, segment, value) {
  const {
    name,
    rowIdx
  } = segment;
  const isArray = isArrayFieldName(name);
  if (isArray) {
    if (rowIdx !== null) {
      const item = convertNode(value, true);
      if (!Array.isArray(result[name])) {
        result[name] = [];
      }
      const arr = result[name];
      while (arr.length <= rowIdx) arr.push(undefined);
      arr[rowIdx] = item;
    } else {
      result[name] = convertNode(value, true);
    }
    return;
  }

  // 对象形：剥掉行号语义（与原 isPlainIndexedObject 行为一致）
  result[name] = convertNode(value, false);
}

/**
 * 反向操作：把嵌套对象/数组拍平成带 `@N` 后缀的扁平结构，key 用点号串联嵌套路径。
 * 与 `convertIndexedFormJson` 对偶（点号复合 key 版本）。
 *
 *   - 父字段名以 items/list 结尾 → 数组的每一行元素被拍平到外层 key 加 `.<子字段>@<行号>`
 *   - 父字段名不以 items/list 结尾 → 对象子键拍平成 `.<子字段>@0`
 *   - 顶层标量：`<key>@0`
 */
export function flattenToIndexedFormJson(input) {
  return flattenNode(input, '');
}

/**
 * 把"扁平 ID/Name 配对对象"转成"按对分行的列表"。
 *
 * 扁平对象形如：
 *   {
 *     "deptApproveName": "李中坤",
 *     "deptApprove": "19911335-...",
 *     "gmApproveName": "李伟",
 *     "gmApprove": "10f82154-...",
 *   }
 *
 * 转换规则：找成对的 `xxxName / xxx`（前缀相同 + 后缀匹配）。
 *   1. 优先识别以 `Name` 结尾的 key；从这种 key 切掉尾部 `Name` 得到 idKey；
 *      若对应的 idKey 也在对象里，凑成一行。
 *   2. 不带 `Name` 后缀的 key（如 `deptApprove` 单独存在）跳过——
 *      避免把只有 id 没有 name 的不完整对当成一行（避免 undefined nameValue）。
 *   3. 用 `seen` 集合去重，防止同一对被重复计入。
 *
 * 输出形如：
 *   [
 *     { idKey: 'deptApprove', nameKey: 'deptApproveName',
 *       idValue: '19911335-...', nameValue: '李中坤' },
 *     { idKey: 'gmApprove', nameKey: 'gmApproveName',
 *       idValue: '10f82154-...', nameValue: '李伟' },
 *   ]
 */

export function toApproverPairs(flat) {
  const seen = new Set();
  const result = [];
  for (const nameKey of Object.keys(flat)) {
    if (seen.has(nameKey)) continue;
    if (!nameKey.endsWith('Name')) continue;
    const idKey = nameKey.slice(0, -'Name'.length);
    if (!idKey || !(idKey in flat)) continue;
    seen.add(nameKey);
    seen.add(idKey);
    result.push({
      idKey,
      nameKey,
      idValue: String(flat[idKey] ?? ''),
      nameValue: String(flat[nameKey] ?? '')
    });
  }
  return result;
}

/**
 * 把对象 value 拍平到 out 中。parentPath 是当前层在扁平 key 里的前缀（含末尾点号）。
 *   - 标量子键 → `<parentPath><key>@0`
 *   - 数组形子键（items/list）→ 子项字段按行号 N 拼成 `<parentPath><key>.<子字段>@N` 写入 out
 *   - 普通对象子键 → 递归进入，prefix 加 `<key>.`，子键内部仍按行号 0 写
 */
function flattenNode(value, parentPath) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  const obj = value;
  const out = {};
  for (const key of Object.keys(obj)) {
    const v = obj[key];
    const seg = `${parentPath}${key}`;
    if (Array.isArray(v)) {
      // items/list 形：把数组各项展开到 out，每个对象元素对应一行 @N。
      v.forEach((item, idx) => {
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          // 递归拍平这一行（带 parentPath），递归内部不带行号；
          // 外层再统一按行号 idx 加 @N。
          const subOut = flattenNodeWithoutRowIdx(item, `${seg}.`);
          Object.keys(subOut).forEach(subKey => {
            out[`${subKey}@${idx}`] = subOut[subKey];
          });
        } else {
          out[`${seg}@${idx}`] = item;
        }
      });
    } else if (v && typeof v === 'object') {
      // 普通对象子字段（depter 这种）：递归进入，子键加 `<key>.` 前缀，
      // 叶子标量在最终 out 里写 `<key>.<sub>@0`。
      // 递归调用 flattenNode 时内部已自动给叶子标量加 @0，外层直接透传
      // subOut 即可——不能再加一次 @0，否则会变成 `<sub>@0@0`。
      const subOut = flattenNode(v, `${seg}.`);
      Object.keys(subOut).forEach(subKey => {
        out[subKey] = subOut[subKey];
      });
    } else {
      // 顶层/中间层标量：对象形父节点加 @0
      out[`${seg}@0`] = v;
    }
  }
  return out;
}

/**
 * 同 flattenNode，但叶子标量不写 `@0` —— 给数组行展开用的中间步骤，
 * 由调用方按行号统一补 @N 后缀。
 */
function flattenNodeWithoutRowIdx(value, parentPath) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  const obj = value;
  const out = {};
  for (const key of Object.keys(obj)) {
    const v = obj[key];
    const seg = `${parentPath}${key}`;
    if (Array.isArray(v)) {
      // 嵌套数组：按行号归位到 out，行号继续由外层统一追加
      v.forEach((item, idx) => {
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          const subOut = flattenNodeWithoutRowIdx(item, `${seg}.`);
          Object.keys(subOut).forEach(subKey => {
            out[`${subKey}@${idx}`] = subOut[subKey];
          });
        } else {
          out[`${seg}@${idx}`] = item;
        }
      });
    } else if (v && typeof v === 'object') {
      const subOut = flattenNodeWithoutRowIdx(v, `${seg}.`);
      Object.keys(subOut).forEach(subKey => {
        out[`${subKey}@0`] = subOut[subKey];
      });
    } else {
      // 不加 @0，由调用方补
      out[seg] = v;
    }
  }
  return out;
}

/**
 * 行是否为空行：所有字段都是 undefined 或 null。
 * （form 未填的行会按行号展开成 `{name: undefined, type: undefined, ...}`）
 */
function isEmptyRow(row) {
  const keys = Object.keys(row);
  if (keys.length === 0) return true;
  for (const k of keys) {
    const v = row[k];
    if (v !== undefined && v !== null) return false;
  }
  return true;
}

/**
 * 把一段含 JSX / TS 的源码转成「接收外部依赖、返回 JSX」的纯函数字符串。
 *
 * 用 `new Function` 注入依赖（Form、Input），避免走 eval + 模块系统（babel/standalone
 * 默认脚本解析，无法处理 import/export，会抛
 * "Cannot use import statement outside a module"）。
 *
 * - 只用 preset-react 转 JSX → React.createElement，不上 preset-env（避免 import → require 链路）。
 * - 调用方需要传入依赖作为参数；函数体内通过参数名访问，不要再 import。
 */

/**
 * 剥掉所有 import / export 语句。
 * 覆盖：
 *  - 整行 import / export（含/不含分号）
 *  - 多行 import {...} from 'xxx';
 *  - import 'side-effect-only';
 *  - export default / export const
 *  - html2jsx 函数体内残留的 import 语句
 * 重要：必须用非贪婪匹配 (.*?) + [\s\S]*? 处理多行 import。
 */
function stripModuleSyntax(code) {
  let out = code;
  // 1. 多行 import {...} from 'xxx'; 或 import X from 'xxx';
  out = out.replace(/import\s+(?:[\s\S]*?)\s+from\s+['"][^'"]+['"]\s*;?/g, '');
  // 2. 单行 import 'xxx'; （side-effect-only）
  out = out.replace(/import\s+['"][^'"]+['"]\s*;?/g, '');
  // 3. import X = require('xxx');（TS 的 import-equals 语法）
  out = out.replace(/import\s+\w+\s*=\s*require\s*\([^)]+\)\s*;?/g, '');
  // 4. export default / export const / export function
  out = out.replace(/export\s+default\s+/g, '');
  out = out.replace(/export\s+(const|let|var|function|class)\s+/g, '$1 ');
  return out;
}
function compileToFactory(code, deps) {
  const cleaned = stripModuleSyntax(code);
  // html2jsx 出来的形状是 `function excelTpl () { ... return <JSX /> }`
  // 我们只需要 return 的 JSX 表达式。匹配不到就当整段就是表达式。
  const fnMatch = cleaned.match(/function\s+\w+\s*\([^)]*\)\s*\{([\s\S]*)\}\s*$/);
  const body = fnMatch ? fnMatch[1] : cleaned;
  const returnMatch = body.match(/return\s+([\s\S]*?);?\s*$/);
  const expr = returnMatch ? returnMatch[1].trim().replace(/;\s*$/, '') : body.trim();

  // babel transform 用 presets: ['react'] 只转 JSX → React.createElement。
  // 强制 runtime: 'classic' 避免 babel 自动注入 `import { jsx } from 'react/jsx-runtime'`
  // （automatic runtime 默认行为）—— 该 import 在 new Function 上下文里非法。
  // classic runtime 输出 `React.createElement(...)`，调用方通过 deps 注入 React。
  const compiled = stripModuleSyntax(transform(expr, {
    presets: [['react', {
      runtime: 'classic'
    }]],
    filename: 'inline.tsx'
  }).code);

  // babel 输出末尾会自带 `;`，但箭头函数体要求是单一表达式。
  // 必须 strip 掉尾部 `;`，否则会变成 `=> ( ... ;)`，语法非法。
  const stripped = compiled.replace(/;\s*$/, '');
  // 拼成 `return ((deps) => ( <compiled_expr> ))(deps)` 形式，
  // 让调用方用 new Function 注入运行时依赖
  return `return ((${deps.join(', ')}) => (${stripped}))(${deps.join(', ')});`;
}
export function runReactCode(code, _runPath, deps) {
  const factoryBody = compileToFactory(code, Object.keys(deps));
  const fn = new Function(...Object.keys(deps), factoryBody);
  return fn(...Object.values(deps));
}

/**
 * 提取样式标签
 * @param htmlString
 * @returns
 */
export function extractAndRemoveStyleTag(inputString) {
  inputString = inputString.replace(/<br>/g, () => {
    return '<br class="lx-br">';
  });
  const styleTagRegex = /<style>[\s\S]*?<\/style>/;
  const match = inputString.match(styleTagRegex);
  if (match) {
    const [styleTag] = match;
    const stringWithoutStyle = inputString.replace(styleTag, '');
    return {
      styleTag: styleTag,
      stringWithoutStyle: stringWithoutStyle
    };
  } else {
    return {
      styleTag: null,
      stringWithoutStyle: inputString
    };
  }
}

// 生成key
export function getKey(type, name) {
  type = type?.trim() || '';
  if (type) {
    name = `${type}.${name}`;
  }
  return name || '';
}

/**
 * 表单验证规则
 * @param required
 * @returns
 */
function getRules(required = false) {
  return `[{ required: ${required}, message: '' }]`;
}

/**
 * 删除字符串${*}
 * @param str
 * @param target
 * @returns
 */
export function removeSubstring(str, target, component = '') {
  return str.replace(new RegExp(`\\$\\{${target}\\}`, 'g'), () => {
    return component;
  });
}
export function getJsx(content, data) {
  const {
    styleTag,
    stringWithoutStyle
  } = extractAndRemoveStyleTag(content);
  const parser = new DOMParser();
  const dom = parser.parseFromString(stringWithoutStyle, 'text/html');
  let component = `<></>`;

  // 处理html的全面组件和数据开始
  const divList = Array.from(dom.querySelectorAll('.lx-div'));
  const spanList = Array.from(dom.querySelectorAll('.lx-span'));
  const nameCountMap = new Map(); // 记录每个原始 name 出现的次数

  [...divList, ...spanList].forEach(div => {
    if (div.querySelectorAll('.lx-span')?.length) {
      return;
    }
    let {
      name
    } = div.dataset;
    const {
      id,
      type
    } = div.dataset;
    if (!name) return; // 没有 name 则跳过
    const key = getKey(type, name);
    const field = flowExcelStore.uniqueMap.get(key) || {};
    const rule = getRules(field.required);
    if (!nameCountMap.has(key) || field?.type === RULE_TYPES.RADIO.value) {
      nameCountMap.set(key, 0);
      name = `${key}@0`;
    } else {
      nameCountMap.set(key, nameCountMap.get(key) + 1);
      name = `${key}@${nameCountMap.get(key)}`;
    }

    // 添加下面替换编辑
    if (!div.innerText.trim()) {
      div.innerText = `\${${field?.name || ''}}`;
    }

    // 当前需要填写字段
    if (flowExcelStore.currentFieldsMap.has(key)) {
      // 此处 name 一定不为空字符串（上面 if (!name) return 已守住）
      component = formMap[field?.type]?.(name, rule, field, id);
    } else {
      // console.log('------name------>', name);
      const viewRenderer = viewMap[field?.type];
      if (viewRenderer) {
        component = viewRenderer(data || {}, name, field, id);
      } else {
        const cellValue = data?.[name] || '';
        component = `<>${cellValue === 0 ? cellValue : cellValue || '&nbsp;'}</>`;
      }
    }
    div.innerText = removeSubstring(div.innerText, field?.name, component);
  });
  let result = dom.querySelector('body')?.innerHTML || '';

  // console.log('result', result);

  result = html2jsx(result, {
    name: 'excelTpl'
  });

  // 不再做 function-name → export default 的文本改写：
  // runReactCode 现在直接抽取 return 表达式拼成 IIFE，自己管理依赖注入。

  // 处理特殊字符
  result = result.replace(/&lt;|&gt;|<br \/>|class=/g, match => {
    if (match === '&lt;') {
      return '<';
    } else if (match === '&gt;') {
      return '>';
    } else if (match === '<br />') {
      return ' ';
    } else {
      return 'className=';
    }
  });
  const programCode = runReactCode(result, 'index.tsx', {
    Form: _Form,
    Input: _Input,
    Image: _Image,
    LxInputNumber,
    DatePicker,
    ListSelector,
    EnumSelector,
    FilesUpload,
    LongText,
    React,
    flowExcelStore,
    Decimal,
    formulas,
    RadioGroup,
    RadioIcon,
    EmptyCheckBoxIcon
  });
  return {
    jsx: programCode,
    style: styleTag
  };
}